import Cookies from "js-cookie";
import {
  buildResolvePath,
  getProjectGroupingFromCookies,
  LAST_ORG_COOKIE,
  ORG_COOKIE,
  TEAM_COOKIE,
} from "@/lib/auth/resolveDestination.shared";
import { RETURN_TO_PARAM } from "@/lib/completeProfileReturnTo";
import { TRIAL_UPLOAD_ONBOARDING_PATH } from "./postLogin";
import { resolvePostLoginDestination } from "./resolvePostLoginDestination.client";
import { getSafeReturnTo } from "./validateReturnTo";

export type ResolveClientResult =
  | { ok: true; path: string }
  | { ok: false; reason: "no_access" | "auth_unavailable" | "account_disabled" };

// Maps a backend JSON:API error `code` to the frontend outcome it represents (see
// TrimbleAuthDisabledException, AccountDisabledException, and JwtAuthGuard.handleRequest
// on the API side for where each code comes from). A code absent from this map — or no
// code at all — defaults to "auth_unavailable" wherever that default is used below.
const AUTH_ERROR_REASONS: Record<string, "auth_unavailable" | "account_disabled"> = {
  trimble_auth_disabled: "auth_unavailable",
  account_disabled: "account_disabled",
  token_expired: "auth_unavailable",
  token_invalid: "auth_unavailable",
  token_not_yet_valid: "auth_unavailable",
  unauthenticated: "auth_unavailable",
};

// Thrown internally to short-circuit resolveClientDestination when a request failed for
// an auth/system reason rather than the user genuinely having no organizations.
class AuthFailureError extends Error {
  constructor(readonly reason: "auth_unavailable" | "account_disabled") {
    super(reason);
  }
}

async function readErrorCode(resp: Response): Promise<string | undefined> {
  try {
    const body = await resp.json();
    return typeof body?.errors?.[0]?.code === "string" ? body.errors[0].code : undefined;
  } catch {
    return undefined;
  }
}

// For per-resource checks (validateOrgAccess): an uncoded 403/404 is a legitimate "no
// access to this specific org" business outcome (a normal CASL/policy denial) and must
// fall through to the caller's candidate-fallback logic, not be treated as an auth
// failure. A 401 is never resource-specific — JwtAuthGuard rejects before any
// per-resource authorization check even runs — so it's always an auth failure regardless
// of whether it carries a recognized code. Anything else non-ok (5xx, 429, ...) is a
// system problem, never a legitimate per-resource signal.
async function checkScopedAuthFailure(resp: Response): Promise<void> {
  if (resp.ok) return;
  if (resp.status === 401) {
    const code = await readErrorCode(resp);
    throw new AuthFailureError(AUTH_ERROR_REASONS[code ?? ""] ?? "auth_unavailable");
  }
  if (resp.status === 403) {
    const code = await readErrorCode(resp);
    if (code && AUTH_ERROR_REASONS[code]) {
      throw new AuthFailureError(AUTH_ERROR_REASONS[code]);
    }
    return;
  }
  if (resp.status === 404) return;
  throw new AuthFailureError("auth_unavailable");
}

// For unscoped list lookups (fetchFirstOrganizationId, fetchFirstTeamIdForOrganization):
// there's no specific resource being checked, so a non-2xx is an auth/system problem —
// except for a 403 carrying no recognized auth code. That is a CASL/policy denial
// (@Protect(Action.Read, 'ORGANIZATION') refusing a principal that holds no role grants),
// which is the same "no access" business outcome an empty list represents, not a failure
// to authenticate. It falls through so the caller resolves to no_access, matching the sibling
// scoped check. A 401 is never a policy denial (JwtAuthGuard rejects before any
// authorization check runs), so it always throws.
async function checkUnscopedAuthFailure(resp: Response): Promise<void> {
  if (resp.ok) return;
  const code = await readErrorCode(resp);
  const reason = AUTH_ERROR_REASONS[code ?? ""];
  if (resp.status === 403 && !reason) return;
  throw new AuthFailureError(reason ?? "auth_unavailable");
}

async function validateOrgAccess(orgId: string, headers: HeadersInit): Promise<boolean> {
  const resp = await fetch(`/api/proxy/api/v1/organizations/${orgId}`, { headers, cache: "no-store" });
  await checkScopedAuthFailure(resp);
  return resp.ok;
}

/**
 * Whether the authenticated user has NOT yet accepted DC's terms. This is a
 * narrow check of `acceptedTerms` only — it is NOT a "should we onboard?"
 * gate. The decision to route a user to /complete-profile is the caller's, and
 * is Trimble-specific: only `TrimbleResolvePage` (which renders solely in
 * Trimble mode) combines this with the active backend. The auth backend is
 * determined by a LaunchDarkly flag / cookie, not a field on `/users/me`, so it
 * can't be re-derived here — keep the backend guard at the call site.
 *
 * Fails open (returns false) on any error so a transient /me failure never
 * blocks sign-in; the next full page load re-runs the resolve and catches it.
 */
export async function fetchHasUnacceptedTerms(token: string): Promise<boolean> {
  try {
    const resp = await fetch("/api/proxy/api/v1/users/me", {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      cache: "no-store",
    });
    if (!resp.ok) return false;
    const data = await resp.json();
    return data?.data?.attributes?.acceptedTerms !== true;
  } catch {
    return false;
  }
}

async function fetchFirstOrganizationId(headers: HeadersInit): Promise<string | null> {
  const resp = await fetch("/api/proxy/api/v1/organizations?page[size]=1&sort=name", { headers, cache: "no-store" });
  await checkUnscopedAuthFailure(resp);
  if (!resp.ok) return null;
  const data = await resp.json();
  return data?.data?.[0]?.id ?? null;
}

async function fetchFirstTeamIdForOrganization(orgId: string, headers: HeadersInit): Promise<string | null> {
  const params = new URLSearchParams();
  params.set("page[size]", "1");
  params.set("sort", "name");
  params.set("filter[scope.parent.organization.id]", orgId);
  const resp = await fetch(`/api/proxy/api/v1/teams?${params.toString()}`, { headers, cache: "no-store" });
  await checkUnscopedAuthFailure(resp);
  if (!resp.ok) return null;
  const data = await resp.json();
  return data?.data?.[0]?.id ?? null;
}

export async function resolveClientDestination(
  token: string,
  returnTo?: string,
  preferredOrgId?: string,
): Promise<ResolveClientResult> {
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const targetPath = (returnTo || "files").replace(/^\/+/, "");

  try {
    let orgId: string | null = null;
    let teamId: string | null = null;

    // Invite landing: prefer the invited org (when accessible) over the cookie chain,
    // so an existing user lands in the org they were just invited to rather than their
    // last-active org. Leave teamId null so a team is picked for the preferred org.
    if (preferredOrgId && (await validateOrgAccess(preferredOrgId, headers))) {
      orgId = preferredOrgId;
    }

    if (!orgId) {
      orgId = Cookies.get(ORG_COOKIE) ?? null;
      teamId = Cookies.get(TEAM_COOKIE) ?? null;
      const lastOrgId = Cookies.get(LAST_ORG_COOKIE) ?? null;
      let inaccessibleOrgId: string | null = null;

      if (orgId) {
        const isAccessible = await validateOrgAccess(orgId, headers);
        if (!isAccessible) {
          inaccessibleOrgId = orgId;
          orgId = null;
          teamId = null;
        }
      }

      if (!orgId && lastOrgId && lastOrgId !== inaccessibleOrgId) {
        const isAccessible = await validateOrgAccess(lastOrgId, headers);
        if (isAccessible) {
          orgId = lastOrgId;
          teamId = null;
        }
      }
    }

    if (!orgId) {
      orgId = await fetchFirstOrganizationId(headers);
    }

    if (!orgId) {
      return { ok: false, reason: "no_access" };
    }

    if (!teamId) {
      teamId = await fetchFirstTeamIdForOrganization(orgId, headers);
    }

    if (!teamId) {
      return { ok: false, reason: "no_access" };
    }

    const requestedPath = targetPath.split("?")[0];
    let landingPath = targetPath;
    if (requestedPath === "files") {
      const onboardingPath = await resolvePostLoginDestination(`Bearer ${token}`, {
        organizationId: orgId,
        teamId,
      });
      if (onboardingPath === TRIAL_UPLOAD_ONBOARDING_PATH) landingPath = "chat";
    }

    const groupBy =
      landingPath === "files" ? getProjectGroupingFromCookies(orgId, teamId, (name) => Cookies.get(name)) : null;

    return { ok: true, path: buildResolvePath(orgId, teamId, landingPath, groupBy) };
  } catch (err) {
    if (err instanceof AuthFailureError) {
      return { ok: false, reason: err.reason };
    }
    throw err;
  }
}

export type ResolveTrimbleDestinationResult =
  | { kind: "no_access" }
  | { kind: "auth_unavailable" }
  | { kind: "account_disabled" }
  | { kind: "redirect"; path: string };

/**
 * Combines org/team resolution with the terms-acceptance gate into the single
 * decision callers need to navigate a freshly-authenticated Trimble user.
 * Shared by the sign-in callback (default-target path) and the standalone
 * /resolve entry point (bare authenticated URLs) so the two never drift.
 */
export async function resolveTrimbleDestination(
  token: string,
  returnTo?: string,
  preferredOrgId?: string,
): Promise<ResolveTrimbleDestinationResult> {
  const result = await resolveClientDestination(token, returnTo, preferredOrgId);
  if (!result.ok) return { kind: result.reason };

  const hasUnacceptedTerms = await fetchHasUnacceptedTerms(token);
  if (hasUnacceptedTerms) {
    const requestedHome = (returnTo || "files").replace(/^\/+/, "").split("?")[0] === "files";
    const onboardingLanding = requestedHome && result.path.endsWith("/chat") ? result.path : null;
    const rawReturn = onboardingLanding ?? (returnTo?.startsWith("/") ? returnTo : returnTo ? `/${returnTo}` : null);
    const safeReturn = getSafeReturnTo(rawReturn);
    return { kind: "redirect", path: `/complete-profile?${RETURN_TO_PARAM}=${encodeURIComponent(safeReturn)}` };
  }

  return { kind: "redirect", path: result.path };
}
